import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { breakdownClipTypesApi } from '../services/api';
import { BreakdownClipType, CreateBreakdownClipTypeData } from '../types';

const BreakdownClipTypesManagement: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingClipType, setEditingClipType] = useState<BreakdownClipType | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const queryClient = useQueryClient();

  const { data: clipTypesData, isLoading } = useQuery({
    queryKey: ['breakdown-clip-types', { page, search }],
    queryFn: () => breakdownClipTypesApi.getAll({ page, limit: 10, search: search || undefined }),
  });

  const { register, handleSubmit, reset, formState: { errors } } = useForm<CreateBreakdownClipTypeData>();

  const createMutation = useMutation({
    mutationFn: breakdownClipTypesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['breakdown-clip-types'] });
      reset();
      setShowForm(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: CreateBreakdownClipTypeData }) =>
      breakdownClipTypesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['breakdown-clip-types'] });
      reset();
      setEditingClipType(null);
      setShowForm(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: breakdownClipTypesApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['breakdown-clip-types'] });
    },
  });

  const onSubmit = (data: CreateBreakdownClipTypeData) => {
    if (editingClipType) {
      updateMutation.mutate({ id: editingClipType.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleEdit = (clipType: BreakdownClipType) => {
    setEditingClipType(clipType);
    reset({ name: clipType.name, description: clipType.description || '' });
    setShowForm(true);
  };

  const handleDelete = (clipType: BreakdownClipType) => {
    if (window.confirm(`Are you sure you want to delete "${clipType.name}"?`)) {
      deleteMutation.mutate(clipType.id);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingClipType(null);
    reset();
  };

  return (
    <div className="breakdown-clip-types-management">
      <div className="page-header">
        <h1>Breakdown Clip Types Management</h1>
        <button className="btn btn-primary" onClick={() => setShowForm(true)} disabled={showForm}>
          Add New Clip Type
        </button>
      </div>

      {showForm && (
        <div className="form-container">
          <h2>{editingClipType ? 'Edit Clip Type' : 'Create New Clip Type'}</h2>
          <form onSubmit={handleSubmit(onSubmit)} className="admin-form">
            <div className="form-group">
              <label htmlFor="name">Name *</label>
              <input
                {...register('name', { required: 'Name is required' })}
                type="text"
                id="name"
                className={errors.name ? 'error' : ''}
              />
              {errors.name && <span className="error-text">{errors.name.message}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea {...register('description')} id="description" rows={3} />
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {editingClipType ? 'Update' : 'Create'} Clip Type
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="table-controls">
        <input
          type="text"
          placeholder="Search clip types..."
          aria-label="Search clip types"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="search-input"
        />
      </div>

      {isLoading ? (
        <div className="loading">Loading clip types...</div>
      ) : clipTypesData && clipTypesData.data.length > 0 ? (
        <>
          <div className="table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Breakdowns</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {clipTypesData.data.map((clipType) => (
                  <tr key={clipType.id}>
                    <td><strong>{clipType.name}</strong></td>
                    <td>{clipType.description || '-'}</td>
                    <td>{clipType.breakdownsCount || 0}</td>
                    <td>{new Date(clipType.createdAt).toLocaleDateString()}</td>
                    <td>
                      <div className="action-buttons">
                        <button className="btn btn-small btn-secondary" onClick={() => handleEdit(clipType)}>Edit</button>
                        <button className="btn btn-small btn-danger" onClick={() => handleDelete(clipType)} disabled={deleteMutation.isPending}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {clipTypesData.pagination.pages > 1 && (
            <div className="pagination">
              {Array.from({ length: clipTypesData.pagination.pages }, (_, i) => i + 1).map((pageNum) => (
                <button key={pageNum} className={`btn btn-small ${pageNum === page ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setPage(pageNum)}>
                  {pageNum}
                </button>
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="empty-state">
          <p>No breakdown clip types found. Create your first clip type!</p>
        </div>
      )}
    </div>
  );
};

export default BreakdownClipTypesManagement;
